import { Fragment, ReactElement, useMemo } from 'react';
import {
  FamilyFatherNotificationsCrumbs,
  FamilyMotherNotificationsCrumbs,
  FamilyNotificationsCrumbs,
  FamilyOtherNotificationsCrumbs,
} from 'notifications-from-civil-status/constants/breadcrumb';
import { IdAmbito } from 'shell/constants/id-ambito';
import {
  FamilyFatherNotificationsSubscriptionCrumbs,
  FamilyMotherNotificationsSubscriptionCrumbs,
  FamilyNotificationsSubscriptionCrumbs,
  FamilyOtherNotificationsSubscriptionCrumbs,
} from 'subscription/constants/breadcrumb';
import {
  FamilyFatherNotificationsMutationCrumbs,
  FamilyMotherNotificationsMutationCrumbs,
  FamilyNotificationsMutationCrumbs,
  FamilyOtherNotificationsMutationCrumbs,
} from 'mutation/constants/research-notifications-civil-status.crumbs';
import {
  FamilyFatherNotificationsCancellationsCrumbs,
  FamilyMotherNotificationsCancellationsCrumbs,
  FamilyNotificationsCancellationsCrumbs,
  FamilyOtherNotificationsCancellationsCrumbs,
} from 'cancellations/constants/breadcrumb';
import { getBaseUrl } from 'notifications-from-civil-status/utils/get-base-url';
import { useParams } from 'react-router-dom';
import { useEventCallback } from '@mui/material';
import { FamilyNotificationsCivilStatusCrumbs } from 'civil-status/constants/breadcrumb';
import ResearchFamilyNotificationsShared from '../../research-notifications/research-family';

interface ResearchFamilyNotificationsCivilStatusSharedProps {
  scope?: string;
  isOther?: boolean;
  isMother?: boolean;
  isFather?: boolean;
}

const ResearchFamilyNotificationsCivilStatusShared = ({
  scope,
  isOther,
  isMother,
  isFather,
}: ResearchFamilyNotificationsCivilStatusSharedProps): ReactElement => {
  const params = useParams();

  const idAnsc = params.id || undefined;
  const idNotifica = params.idNotifica || '';
  const idCat = params.idCat || undefined;

  const goBackUrl: string = useMemo(() => {
    return `${getBaseUrl(scope, idCat)}/modifica/${idAnsc}/${idNotifica}`;
  }, [scope, idAnsc, idCat]);

  const redirectUrl: string = useMemo(() => {
    let redirectUrlRes = `${goBackUrl}/famiglia`;

    if (isOther) {
      redirectUrlRes = `${redirectUrlRes}/altra`;
    }

    if (isMother) {
      redirectUrlRes = `${redirectUrlRes}/madre`;
    }

    if (isFather) {
      redirectUrlRes = `${redirectUrlRes}/padre`;
    }

    return `${redirectUrlRes}/dettaglio`;
  }, [goBackUrl, isOther, isMother, isFather]);

  const getItems = useEventCallback(() => {
    if (idCat) {
      return FamilyNotificationsCivilStatusCrumbs(goBackUrl, idCat);
    }

    if (!scope) {
      return FamilyNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  });

  const getItemsOther = useEventCallback(() => {
    if (!scope) {
      return FamilyOtherNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyOtherNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyOtherNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyOtherNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  });

  const getItemsMother = useEventCallback(() => {
    if (!scope) {
      return FamilyMotherNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyMotherNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyMotherNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyMotherNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  });

  const getItemsFather = useEventCallback(() => {
    if (!scope) {
      return FamilyFatherNotificationsCrumbs(goBackUrl);
    }

    switch (scope) {
      case IdAmbito.Iscrizioni:
        return FamilyFatherNotificationsSubscriptionCrumbs(goBackUrl);
      case IdAmbito.Mutazioni:
        return FamilyFatherNotificationsMutationCrumbs(goBackUrl);
      case IdAmbito.Cancellazioni:
        return FamilyFatherNotificationsCancellationsCrumbs(goBackUrl);
      default:
        return [];
    }
  });

  const items = useMemo(() => {
    if (isOther) {
      return getItemsOther();
    }

    if (isMother) {
      return getItemsMother();
    }

    if (isFather) {
      return getItemsFather();
    }

    return getItems();
  }, [scope, isOther, isMother, isFather]);

  return (
    <Fragment>
      <ResearchFamilyNotificationsShared
        goBackUrl={goBackUrl}
        redirectUrl={redirectUrl}
        items={items}
      />
    </Fragment>
  );
};

ResearchFamilyNotificationsCivilStatusShared.displayName =
  'Research Family Notifications CivilStatusShared component';

export default ResearchFamilyNotificationsCivilStatusShared;
